/**
 * @file RepMobileHeader.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-09-03
 *
 * The condensed top bar for the rep phone shell, replacing the desktop status
 * bar. That bar is `position: fixed; bottom: 0; height: 40px` with a separate
 * brand block beside it — on a phone it would collide with the tab bar and with
 * the iOS home indicator, so the phone shell renders this instead, at the top.
 *
 * What survives from the desktop bar, and why:
 *   - the page title, so a rep knows where they are without a breadcrumb
 *   - "View only", as a glyph. This is the one status a rep must always be able
 *     to see; a control that fails silently is the thing it prevents.
 *
 * What is dropped: the email address, the YOKE wordmark and the version (all
 * moved into the More sheet), and the Xero pill — a rep's xero requirement is
 * 'blocked', so it never rendered for them anyway.
 */

import React from 'react';
import { isReadOnly } from '../utils/permissions';
import './RepMobileHeader.css';

/**
 * Morning / Afternoon / Evening, on the device's own clock.
 *
 * Deliberately the phone's local time rather than the server's: the greeting is
 * a courtesy to the person holding it, not a statement about the system. (The
 * maintenance countdown is the opposite case and uses the server clock, because
 * there being one correct answer matters there.)
 */
export function greetingFor(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Morning';
  if (h < 18) return 'Afternoon';
  return 'Evening';
}

function RepMobileHeader({ user, title, onOpenProfile }) {
  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase() || '?';

  return (
    <header className="rep-mobile-header">
      <div className="rep-header-title">{title || 'Excel Agri'}</div>

      <div className="rep-header-right">
        {isReadOnly(user) && (
          <span
            className="rep-header-readonly"
            title="Your account can view records but not change them"
            aria-label="View only"
          >
            <span className="material-symbols-rounded">visibility</span>
          </span>
        )}
        <button
          type="button"
          className="rep-header-avatar"
          onClick={onOpenProfile}
          aria-label="Profile"
        >
          {initials}
        </button>
      </div>
    </header>
  );
}

export default RepMobileHeader;
